import { useLayoutEffect, useRef } from 'react';
import { Command } from 'cmdk';
import { ParentEmptyState } from './ParentEmptyState';
import { ParentOptionItem } from './ParentOptionItem';
import { SearchFailure } from './SearchResultsList';
import type { ParentOption } from './parentOptions.types';

interface ParentModeListProps {
  query: string;
  isFetching: boolean;
  isShowingRecents: boolean;
  options: readonly ParentOption[];
  onSelect: (option: ParentOption) => void;
  /**
   * The destination search failed. Shown in place of the empty state, and
   * under whatever options remain (the workspace root needs no search), so a
   * failed search never reads as "this collection does not exist".
   */
  failure?: Error | null;
  onRetry?: () => void;
}

/**
 * The move/copy "destination picker" body of the palette (parent-selection
 * mode) — a flat list of ParentOptions. Extracted from CommandPalette so the
 * parent-mode branch doesn't inflate that component's complexity.
 */
export function ParentModeList({
  query,
  isFetching,
  isShowingRecents,
  options,
  onSelect,
  failure,
  onRetry,
}: ParentModeListProps) {
  // A new query starts at the top, as in ExpandedResultGroups.
  const listRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [query]);

  return (
    <Command.List ref={listRef}>
      {!failure && (
        <ParentEmptyState query={query} isFetching={isFetching} hasOptions={options.length > 0} />
      )}

      {options.length > 0 && (
        <Command.Group heading={isShowingRecents ? 'Destinations' : undefined} forceMount>
          {options.map((option) => (
            <ParentOptionItem
              key={`${option.type}-${option.id}`}
              option={option}
              onSelect={() => onSelect(option)}
            />
          ))}
        </Command.Group>
      )}

      <SearchFailure error={failure} onRetry={onRetry} />
    </Command.List>
  );
}
